/** Local mock: uploaded / field-built multi-option proposals + open tracking. */

import { clientSlug, proposalPathForSlug, slugifyClientName } from "./portalPaths";

export const PROPOSAL_UPLOAD_KEY = "taistu_proposal_upload_mock";

/** Timeline choices for the live proposal card (under client name). */
export const PROPOSAL_TIMELINES = [
  "3-4 weeks",
  "3 months",
  "Ongoing",
];

const DEFAULT_TIMELINE = PROPOSAL_TIMELINES[0];

/** Monthly-rate timelines: investment displays as $X/month on the proposal. */
export function isMonthlyProposalTimeline(timeline) {
  const t = normalizeTimeline(timeline);
  return t === "3 months" || t === "Ongoing";
}

function formatProposalMoney(value) {
  const amount = Number(value || 0);
  if (Number.isInteger(amount)) return `$${amount.toLocaleString()}`;
  return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** e.g. "$2,500/month" for 3 months / Ongoing, else "$12,000". */
export function formatProposalInvestment(amount, timeline) {
  const money = formatProposalMoney(amount);
  if (isMonthlyProposalTimeline(timeline)) return `${money}/month`;
  return money;
}

function normalizeTimeline(raw) {
  const t = String(raw || "").trim();
  if (!t) return DEFAULT_TIMELINE;
  if (PROPOSAL_TIMELINES.includes(t)) return t;
  const lower = t.toLowerCase();
  if (/3[\s–-]*4\s*week/i.test(t) || (/week/i.test(lower) && !/month/i.test(lower))) {
    return PROPOSAL_TIMELINES[0];
  }
  if (/3[\s-]*month/i.test(t) || /month engagement/i.test(lower)) return PROPOSAL_TIMELINES[1];
  if (/ongoing|retainer|monthly/i.test(lower)) return PROPOSAL_TIMELINES[2];
  return DEFAULT_TIMELINE;
}

const MOCK_OPTIONS = [
  {
    option_label: "Option 1 · Essential",
    intro:
      "Focused identity phase to ready the line for retail conversations this fall — quieter system, clear direction.",
    scope_items: [
      {
        number: "01",
        title: "Brand Identity System",
        description: "Wordmark, type, and color. Held to material honesty over high contrast.",
      },
    ],
    investment_total: 12000,
    subtotal: 12000,
    total: 12000,
  },
  {
    option_label: "Option 2 · Brand + Web",
    intro:
      "Phased brand and web build designed for Q4 partner pitches — identity through a shop-ready site.",
    scope_items: [
      {
        number: "01",
        title: "Brand Identity System",
        description: "Wordmark, type, color, and packaging cues.",
      },
      {
        number: "02",
        title: "Website Design + Development",
        description: "Product grid, CMS, and path from story to shop.",
      },
    ],
    investment_total: 18000,
    subtotal: 18000,
    total: 18000,
  },
  {
    option_label: "Option 3 · Full system",
    intro:
      "Full phased build through packaging templates for retail-ready samples.",
    scope_items: [
      {
        number: "01",
        title: "Brand Identity System",
        description: "Full system across wordmark, type, color, and packaging cues.",
      },
      {
        number: "02",
        title: "Website Design + Development",
        description: "Product grid, CMS, and path from story to shop.",
      },
      {
        number: "03",
        title: "Packaging Templates",
        description: "Production-ready layouts for the first SKU set.",
      },
    ],
    investment_total: 24000,
    subtotal: 24000,
    total: 24000,
  },
];

/** Known TAĪSTU proposal PDFs → live HTML card fields (same look as review screenshot). */
const KNOWN_PDF_OPTIONS = [
  {
    match: /notion/i,
    sort: 1,
    option_label: "Notion OS",
    investment_total: 1500,
    timeline: "3-4 weeks",
    intro:
      "This proposal outlines a Notion OS — one organized workspace to centralize your operations and run your VIP client accounts, built and ready before your new assistant comes on.",
    scope_items: [
      {
        number: "01",
        title: "Central Workspace + Client Hubs",
        description:
          "A Notion workspace architected around your VIP retainer model, with a hub per client for info, preferences, project tracking, and delegatable research notes.",
      },
      {
        number: "02",
        title: "Calendar + Financials",
        description:
          "A shared calendar with permission controls for your assistant, and a financials hub inside a single business-data center.",
      },
      {
        number: "03",
        title: "Role-Based Views",
        description:
          "Your assistant works in a view distinct from yours, with selective visibility across client information — within Notion’s permission model.",
      },
      {
        number: "04",
        title: "SOPs + Assistant Training",
        description:
          "Documented workflows and a live walkthrough, so your assistant can run the system from day one.",
      },
    ],
  },
  {
    match: /custom/i,
    sort: 2,
    option_label: "Custom OS",
    investment_total: 2500,
    timeline: "3-4 weeks",
    intro:
      "This proposal outlines a Custom OS — a proprietary operations portal on your own domain, built around your VIP client model and owned by you outright.",
    scope_items: [
      {
        number: "01",
        title: "Custom Portal on Your Domain",
        description:
          "A proprietary operations portal, built and owned outright — no third-party template, no subscription sprawl, no dependency.",
      },
      {
        number: "02",
        title: "Role-Based Client Accounts",
        description:
          "True per-client accounts with selective visibility, project and timeline tracking, and dedicated research notes; your assistant sees a permission-scoped view.",
      },
      {
        number: "03",
        title: "AI-Assisted Reporting",
        description:
          "Automated analytics and reporting across your clients’ social pages — cutting the manual research and reporting effort.",
      },
      {
        number: "04",
        title: "Calendar, Financials + Comms",
        description:
          "A shared calendar with granular permissions, a financials hub, and email/communications integration, with assistant onboarding built in.",
      },
    ],
  },
  {
    match: /operational|ecosystem/i,
    sort: 3,
    option_label: "Operational Ecosystem",
    investment_total: 2500,
    investment_label: "$2,500 / month",
    timeline: "3 months",
    intro:
      "This proposal outlines the Operational Ecosystem — operating software, a website, and an elevated client experience, built and adopted across a three-month engagement.",
    scope_items: [
      {
        number: "01",
        title: "Operating Software",
        description:
          "Your Custom OS: per-client accounts, role-based access, shared calendar, financials, AI-assisted reporting, and communications integration.",
      },
      {
        number: "02",
        title: "Website",
        description:
          "A compact, portfolio-forward mini site — the work up top, a short about, your services with the VIP hands-off model as the hero, and an inquiry path that feeds the OS.",
      },
      {
        number: "03",
        title: "Elevated Client Experience",
        description:
          "Onboarding, a communication cadence with response standards so clients never wait, client-facing status visibility, and an offboarding + feedback loop.",
      },
    ],
  },
];

function readAll() {
  try {
    return JSON.parse(window.localStorage.getItem(PROPOSAL_UPLOAD_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function writeAll(all) {
  try {
    window.localStorage.setItem(PROPOSAL_UPLOAD_KEY, JSON.stringify(all));
  } catch (e) {
    console.warn("Could not persist proposal upload mock", e);
  }
}

function hoursAgo(h) {
  return new Date(Date.now() - h * 3600 * 1000).toISOString();
}

/** e.g. Northline Ceramics → NorthlineCeramics.pdf */
export function proposalFileName(clientName) {
  const base = String(clientName || "Prospect").replace(/[^a-zA-Z0-9]/g, "") || "Prospect";
  return `${base}.pdf`;
}

export function proposalSubtitle(optionCount) {
  const n = Number(optionCount) || 0;
  if (n <= 1) return "Proposal • 1:1";
  return `Proposal • ${n} options`;
}

export function uploadShareToken(clientId) {
  return `uploaded-${clientId}`;
}

export function isUploadShareToken(token) {
  return String(token || "").startsWith("uploaded-");
}

export function clientIdFromUploadToken(token) {
  const t = String(token || "");
  if (!t.startsWith("uploaded-")) return null;
  return t.slice("uploaded-".length) || null;
}

export function optionMetaFromFileName(fileName) {
  const raw = String(fileName || "").trim();
  const known = KNOWN_PDF_OPTIONS.find((k) => k.match.test(raw));
  if (known) {
    return {
      option_label: known.option_label,
      investment_total: known.investment_total,
      investment_label: known.investment_label || null,
      sort: known.sort,
      known,
    };
  }
  let base = raw.replace(/\.pdf$/i, "");
  base = base.replace(/^TAISTU[_-]?Proposal[_-]?/i, "");
  base = base.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  const label = base
    ? base.replace(/\b\w/g, (c) => c.toUpperCase())
    : "Option";
  return {
    option_label: label,
    investment_total: 0,
    investment_label: null,
    sort: 50,
    known: null,
  };
}

/** Normalize one option into the live HTML proposal card shape. */
export function normalizeProposalOption(raw, clientName, index = 0) {
  const o = raw && typeof raw === "object" ? raw : {};
  const items = Array.isArray(o.scope_items) ? o.scope_items : [];
  const investment = Number(o.investment_total ?? o.total ?? o.subtotal) || 0;
  const timeline = normalizeTimeline(o.timeline || o.date_range);
  const exchange = String(o.exchange || o.they_provide || "").trim();
  const terms = String(o.terms || "").trim();
  const addonRaw = o.addon && typeof o.addon === "object" ? o.addon : null;
  const addonTitle = String(addonRaw?.title || o.addon_title || "").trim();
  const addonDescription = String(addonRaw?.description || o.addon_description || "").trim();
  const addonAmount = Number(addonRaw?.amount ?? o.addon_amount) || 0;
  const addon =
    addonTitle || addonDescription || addonAmount > 0
      ? {
          title: addonTitle || "Add-on",
          description: addonDescription,
          amount: addonAmount,
        }
      : null;
  return {
    option_label: (o.option_label || "").trim() || `Option ${index + 1}`,
    client_name: (o.client_name || clientName || "Prospect").trim(),
    primary_contact_name: String(
      o.primary_contact_name || o.contact_name || "",
    ).trim() || null,
    timeline,
    date_range: timeline, // BLU / legacy field — same value as timeline
    intro: (o.intro || "").trim(),
    scope_items: items.map((it, i) => ({
      number: String(it?.number || String(i + 1).padStart(2, "0")),
      title: String(it?.title || "").trim(),
      description: String(it?.description || "").trim(),
    })),
    investment_total: investment,
    subtotal: investment,
    total: investment,
    investment_label: isMonthlyProposalTimeline(timeline)
      ? `${formatProposalMoney(investment)}/month`
      : null,
    exchange,
    terms,
    addon,
    // Optional PDF attachment for download/email — not used for review rendering.
    pdf_data_url: o.pdf_data_url || null,
    originalFileName: o.originalFileName || null,
    sort: o.sort ?? index,
  };
}

export function blankProposalOption(clientName, index = 0) {
  return normalizeProposalOption(
    {
      option_label: index === 0 ? "Proposal" : `Option ${index + 1}`,
      intro: "",
      scope_items: [{ number: "01", title: "", description: "" }],
      investment_total: 0,
      timeline: DEFAULT_TIMELINE,
    },
    clientName,
    index,
  );
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

function isPdfFile(file) {
  if (!file) return false;
  const type = String(file.type || "").toLowerCase();
  if (type === "application/pdf") return true;
  return /\.pdf$/i.test(file.name || "");
}

function emptyRow(clientId, clientName, options, contactName = null) {
  return {
    clientId,
    clientName: clientName || "Prospect",
    contactName: (contactName || "").trim() || null,
    slug: slugifyClientName(clientName),
    source: "upload",
    fileName: proposalFileName(clientName),
    uploadedAt: new Date().toISOString(),
    sharedAt: null,
    openedAt: null,
    openCount: 0,
    status: "pending",
    options,
    acceptedAt: null,
    acceptedOptionIndex: null,
    acceptedOptionLabel: null,
    acceptedAddon: null,
    revenueInvoiceId: null,
    changesRequestedAt: null,
    responseNote: null,
    originalFileName: null,
  };
}

/** Seed a realistic uploaded 3-option proposal (local demo HTML card). */
export function ensureMockUploadedProposal(clientId, clientName, contactName = null) {
  if (!clientId || typeof window === "undefined") return null;
  const all = readAll();
  if (all[clientId]) {
    const row = all[clientId];
    const nextName = proposalFileName(row.clientName || clientName);
    let dirty = false;
    if (row.fileName !== nextName) {
      row.fileName = nextName;
      dirty = true;
    }
    const contact = (contactName || "").trim();
    if (contact && row.contactName !== contact) {
      row.contactName = contact;
      dirty = true;
    }
    if (dirty) {
      all[clientId] = row;
      writeAll(all);
    }
    return row;
  }
  const row = {
    ...emptyRow(
      clientId,
      clientName,
      MOCK_OPTIONS.map((o, i) => normalizeProposalOption({ ...o, client_name: clientName }, clientName, i)),
      contactName,
    ),
    uploadedAt: hoursAgo(26),
    sharedAt: hoursAgo(24),
    openedAt: hoursAgo(5),
    openCount: 2,
  };
  all[clientId] = row;
  writeAll(all);
  return row;
}

export function getUploadedProposal(clientId) {
  if (!clientId) return null;
  const row = readAll()[clientId] || null;
  if (!row) return null;
  const nextName = proposalFileName(row.clientName);
  const nextSlug = slugifyClientName(row.clientName);
  let dirty = false;
  if (row.fileName !== nextName) {
    row.fileName = nextName;
    dirty = true;
  }
  if (row.slug !== nextSlug) {
    row.slug = nextSlug;
    dirty = true;
  }
  // Hydrate known PDF uploads that were stored before HTML-field mapping.
  if (Array.isArray(row.options)) {
    const nextOpts = row.options.map((o, i) => {
      const hasBody = (o.intro || "").trim() || (o.scope_items || []).some((s) => (s?.title || "").trim());
      if (hasBody) return normalizeProposalOption(o, row.clientName, i);
      const meta = optionMetaFromFileName(o.originalFileName || "");
      if (!meta.known) return normalizeProposalOption(o, row.clientName, i);
      dirty = true;
      return normalizeProposalOption(
        {
          ...meta.known,
          client_name: row.clientName,
          pdf_data_url: o.pdf_data_url || null,
          originalFileName: o.originalFileName || null,
          sort: meta.sort,
        },
        row.clientName,
        i,
      );
    });
    row.options = nextOpts;
  }
  if (dirty) {
    const all = readAll();
    all[clientId] = row;
    writeAll(all);
  }
  return row;
}

export function getUploadedProposalBySlug(slug) {
  const key = slugifyClientName(slug);
  if (!key) return null;
  const all = readAll();
  for (const row of Object.values(all)) {
    if (!row) continue;
    const rowSlug = row.slug || slugifyClientName(row.clientName);
    if (rowSlug === key) return getUploadedProposal(row.clientId);
  }
  return null;
}

/** Strip local-only fields before persisting options on the server. */
export function optionsPayloadForApi(options) {
  return (options || []).map((o, i) => ({
    option_label: o.option_label,
    intro: o.intro,
    timeline: o.timeline || o.date_range,
    investment_total: o.investment_total ?? o.total ?? o.subtotal ?? 0,
    exchange: o.exchange || o.they_provide || "",
    terms: o.terms || "",
    scope_items: Array.isArray(o.scope_items) ? o.scope_items : [],
    addon: o.addon || null,
    addon_title: o.addon_title,
    addon_description: o.addon_description,
    addon_amount: o.addon_amount,
    sort: o.sort ?? i,
  }));
}

function optionDraftFromStored(o) {
  const addonTitle = o.addon?.title || o.addon_title || "";
  const addonDescription = o.addon?.description || o.addon_description || "";
  const addonAmount = o.addon?.amount
    ? o.addon.amount
    : o.addon_amount
      ? o.addon_amount
      : "";
  const hasAddon = Boolean(
    String(addonTitle).trim() ||
      String(addonDescription).trim() ||
      (addonAmount !== "" && addonAmount != null && Number(addonAmount) > 0),
  );
  return {
    option_label: o.option_label || "",
    intro: o.intro || "",
    timeline: o.timeline || o.date_range || PROPOSAL_TIMELINES[0],
    investment_total: o.investment_total ?? o.total ?? "",
    exchange: o.exchange || o.they_provide || "",
    terms: o.terms || "",
    scope_items: (o.scope_items || []).map((s) => ({
      title: s.title || "",
      description: s.description || "",
    })),
    pdf_data_url: o.pdf_data_url || null,
    originalFileName: o.originalFileName || null,
    addon_enabled: hasAddon,
    addon_title: addonTitle,
    addon_description: addonDescription,
    addon_amount: addonAmount,
  };
}

/** Build admin proposal row from GET /admin/clients payload (Mongo source of truth). */
export function uploadedProposalFromClient(client) {
  if (!client?.id) return null;
  const rawOpts = client.proposal_options;
  if (!Array.isArray(rawOpts) || !rawOpts.length) return null;

  const clientName = client.name || "Prospect";
  const contactName = (client.primary_contact_name || "").trim() || null;
  const options = rawOpts.map((o, i) =>
    normalizeProposalOption(
      {
        ...o,
        primary_contact_name: o.primary_contact_name || o.contact_name || contactName,
      },
      clientName,
      i,
    ),
  );
  if (!options.length) return null;

  const review = client.scope_review && typeof client.scope_review === "object" ? client.scope_review : {};
  return {
    ...emptyRow(client.id, clientName, options, contactName),
    source: "fields",
    uploadedAt: client.proposal_updated_at || review.shared_at || null,
    sharedAt: review.shared_at || null,
    openedAt: null,
    openCount: 0,
    status: review.status || "pending",
    acceptedAt: review.accepted_at || null,
    acceptedOptionIndex: review.accepted_option_index ?? null,
    acceptedOptionLabel: review.accepted_option_label || null,
    acceptedAddon: review.accepted_addon || null,
    changesRequestedAt: review.changes_requested_at || null,
    responseNote: review.response_note || null,
    revenueInvoiceId: review.revenue_invoice_id || null,
  };
}

/** Prefer server proposal when present; merge local PDF attachments. */
export function resolveUploadedProposal(clientId, client) {
  if (!clientId) return null;
  const serverRow = client ? uploadedProposalFromClient(client) : null;
  const localRow = getUploadedProposal(clientId);

  if (!serverRow) return localRow;

  const mergePdfAttachments = (serverOptions) =>
    serverOptions.map((opt, i) => {
      const localOpt =
        localRow?.options?.[i] ||
        (localRow?.options || []).find((o) => o.option_label === opt.option_label);
      if (!localOpt?.pdf_data_url) return opt;
      return {
        ...opt,
        pdf_data_url: localOpt.pdf_data_url,
        originalFileName: localOpt.originalFileName || opt.originalFileName,
      };
    });

  if (!localRow?.options?.length) {
    const all = readAll();
    all[clientId] = serverRow;
    writeAll(all);
    return serverRow;
  }

  const serverUpdated = client?.proposal_updated_at || serverRow.uploadedAt;
  const localUpdated = localRow.uploadedAt;
  const serverWins =
    !localUpdated ||
    !serverUpdated ||
    new Date(serverUpdated).getTime() >= new Date(localUpdated).getTime();

  if (serverWins) {
    const row = {
      ...serverRow,
      options: mergePdfAttachments(serverRow.options),
      openedAt: localRow.openedAt,
      openCount: localRow.openCount || 0,
    };
    const all = readAll();
    all[clientId] = row;
    writeAll(all);
    return row;
  }

  return localRow;
}

/** Draft rows for Edit fields — server first, then local cache. */
export function proposalOptionsDraft(client, uploadedProposal) {
  const source =
    uploadedProposal?.options?.length
      ? uploadedProposal.options
      : Array.isArray(client?.proposal_options) && client.proposal_options.length
        ? client.proposal_options.map((o, i) =>
            normalizeProposalOption(o, client?.name || "Prospect", i),
          )
        : [];
  return source.length ? source.map(optionDraftFromStored) : null;
}

/** Map GET /public/proposal payload into the live review packet shape. */
export function packetFromPublicProposal(data, clientId = null) {
  if (!data) return null;
  const compensationType = data.compensation_type || "cash";
  return {
    clientId,
    clientName: data.client_name || "Prospect",
    contactName: data.primary_contact_name || null,
    compensationType,
    status: data.status || "pending",
    options: Array.isArray(data.options)
      ? data.options.map((o) => ({
          ...o,
          compensation_type: compensationType,
          primary_contact_name:
            o.primary_contact_name || data.primary_contact_name || null,
          contact_name: o.contact_name || data.primary_contact_name || null,
        }))
      : [],
    acceptedAt: data.accepted_at || null,
    signedAt: data.signed_at || data.accepted_at || null,
    signatureName: data.signature_name || null,
    acceptedOptionIndex: data.accepted_option_index ?? null,
    acceptedOptionLabel: data.accepted_option_label || null,
    acceptedAddon: data.accepted_addon || null,
    changesRequestedAt: data.changes_requested_at || null,
    responseNote: data.response_note || null,
    invoiceNumber: data.invoice_number || null,
    invoiceUnlocked: Boolean(
      data.invoice_unlocked ?? (data.status === "accepted" && compensationType !== "barter"),
    ),
    reviewKind: data.review_kind || (compensationType === "barter" ? "agreement" : "proposal"),
    alreadyPaid: Boolean(data.already_paid || data.paid),
    paidAt: data.paid_at || null,
  };
}

/**
 * Upload one or more PDFs → options for the live HTML review card.
 * Known TAĪSTU proposal PDFs map into full field content; others get stubs to edit.
 * PDF bytes are kept only for later download — review always uses HTML fields.
 */
export async function saveUploadedFromFiles(clientId, clientName, fileList, contactName = null) {
  const files = Array.from(fileList || []).filter(isPdfFile);
  if (!clientId || !files.length) {
    throw new Error("Choose one or more PDF files");
  }

  const options = await Promise.all(
    files.map(async (file, i) => {
      const meta = optionMetaFromFileName(file.name);
      const pdf_data_url = await fileToDataUrl(file);
      const known = meta.known;
      if (known) {
        return normalizeProposalOption(
          {
            ...known,
            client_name: clientName,
            pdf_data_url,
            originalFileName: file.name,
            sort: known.sort,
          },
          clientName,
          i,
        );
      }
      return normalizeProposalOption(
        {
          option_label: meta.option_label,
          intro: "",
          scope_items: [{ number: "01", title: "", description: "" }],
          investment_total: meta.investment_total || 0,
          date_range: DEFAULT_TIMELINE,
          pdf_data_url,
          originalFileName: file.name,
          sort: meta.sort ?? 50 + i,
        },
        clientName,
        i,
      );
    }),
  );
  options.sort((a, b) => (a.sort || 0) - (b.sort || 0));

  const all = readAll();
  const prev = all[clientId];
  const row = {
    ...emptyRow(clientId, clientName, options, contactName || prev?.contactName),
    originalFileName: files.map((f) => f.name).join(", "),
  };
  all[clientId] = row;
  writeAll(all);
  return row;
}

/** Create or replace proposal from field input (no PDF required). */
export function saveProposalFromFields(clientId, clientName, optionsInput, contactName = null) {
  if (!clientId) throw new Error("Missing client");
  const options = (optionsInput || [])
    .map((o, i) =>
      normalizeProposalOption(
        {
          ...o,
          addon:
            o.addon ||
            (o.addon_title || o.addon_description || o.addon_amount
              ? {
                  title: o.addon_title,
                  description: o.addon_description,
                  amount: o.addon_amount,
                }
              : null),
        },
        clientName,
        i,
      ),
    )
    .filter(
      (o) =>
        o.option_label ||
        o.intro ||
        o.exchange ||
        o.terms ||
        o.scope_items.some((s) => s.title || s.description),
    );
  if (!options.length) {
    throw new Error("Add agreement or proposal content");
  }
  const all = readAll();
  const prev = all[clientId];
  const row = {
    ...emptyRow(clientId, clientName, options, contactName || prev?.contactName),
    uploadedAt: prev?.uploadedAt || new Date().toISOString(),
    sharedAt: prev?.sharedAt || null,
    openedAt: null,
    openCount: 0,
    status: "pending",
    source: "fields",
  };
  all[clientId] = row;
  writeAll(all);
  return row;
}

export function markProposalShared(clientId) {
  const all = readAll();
  const row = all[clientId];
  if (!row) return null;
  row.sharedAt = row.sharedAt || new Date().toISOString();
  all[clientId] = row;
  writeAll(all);
  return row;
}

/** First open stamps openedAt; every open bumps openCount. */
export function markProposalOpened(clientId) {
  const all = readAll();
  const row = all[clientId];
  if (!row) return null;
  if (!row.openedAt) row.openedAt = new Date().toISOString();
  row.openCount = (Number(row.openCount) || 0) + 1;
  all[clientId] = row;
  writeAll(all);
  return row;
}

export function acceptUploadedProposal(clientId, optionIndex, note, addonSelected = false) {
  const all = readAll();
  const row = all[clientId];
  if (!row) return null;
  const opt = row.options?.[optionIndex];
  row.status = "accepted";
  row.acceptedAt = new Date().toISOString();
  row.acceptedOptionIndex = optionIndex;
  row.acceptedOptionLabel = opt?.option_label || `Option ${optionIndex + 1}`;
  row.acceptedAddon =
    addonSelected && opt?.addon
      ? {
          title: opt.addon.title,
          description: opt.addon.description,
          amount: opt.addon.amount,
        }
      : null;
  row.responseNote = (note || "").trim() || null;
  row.changesRequestedAt = null;
  all[clientId] = row;
  writeAll(all);
  return row;
}

/** Persist linked draft invoice id after accept. */
export function setUploadedProposalInvoiceId(clientId, revenueInvoiceId) {
  const all = readAll();
  const row = all[clientId];
  if (!row) return null;
  row.revenueInvoiceId = revenueInvoiceId || null;
  all[clientId] = row;
  writeAll(all);
  return row;
}

/** Clear accept / changes so the proposal can be reviewed again (keeps options). */
export function clearUploadedProposalDecision(clientId) {
  const all = readAll();
  const row = all[clientId];
  if (!row) return null;
  row.status = "pending";
  row.acceptedAt = null;
  row.acceptedOptionIndex = null;
  row.acceptedOptionLabel = null;
  row.acceptedAddon = null;
  row.changesRequestedAt = null;
  row.responseNote = null;
  row.revenueInvoiceId = null;
  all[clientId] = row;
  writeAll(all);
  return row;
}

export function requestChangesUploadedProposal(clientId, note) {
  const all = readAll();
  const row = all[clientId];
  if (!row) return null;
  row.status = "changes_requested";
  row.changesRequestedAt = new Date().toISOString();
  row.responseNote = (note || "").trim() || null;
  row.acceptedAt = null;
  row.acceptedOptionIndex = null;
  row.acceptedOptionLabel = null;
  row.acceptedAddon = null;
  all[clientId] = row;
  writeAll(all);
  return row;
}

export function resetUploadedProposal(clientId, clientName) {
  const all = readAll();
  delete all[clientId];
  writeAll(all);
  return ensureMockUploadedProposal(clientId, clientName);
}

/** Remove a stored proposal without reseeding demo content. */
export function clearUploadedProposal(clientId) {
  if (!clientId) return null;
  const all = readAll();
  delete all[clientId];
  writeAll(all);
  return null;
}

export function uploadedProposalSharePath(clientOrSlug) {
  if (clientOrSlug && typeof clientOrSlug === "object") {
    return proposalPathForSlug(clientSlug(clientOrSlug) || slugifyClientName(clientOrSlug.name));
  }
  return proposalPathForSlug(clientOrSlug);
}
